import { ReactNode, useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    CircularProgress,
    IconButton,
    TextField,
    Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { navigateFromClick } from "shared/services/navigationClick";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";

interface ReferenceAutocompleteInputProps {
    options: any[];
    loading?: boolean;
    selectedId?: string | null;
    displayField?: string;
    referenceTable?: string;
    onChange: (id: string | null) => void;
    onSearchChange?: (search: string) => void;
    onOpenOptions?: () => void;
    error?: boolean;
    helperText?: ReactNode;
    autoFocus?: boolean;
    showOpenButton?: boolean;
    openButtonTitle?: string;
    disabled?: boolean;
    fieldLabel?: string;
    groupBy?: (option: any) => string;
    showConcreteClass?: boolean;
}

const REFERENCE_LABEL_FALLBACK_FIELDS = [
    "display_value",
    "number_ref",
    "name",
    "label",
    "title",
    "subject",
    "code",
    "email",
    "id",
];

export const getReferenceOptionLabel = (t: TFunction, option: any, displayField?: string, showConcreteClass = false) => {
    if (!option) {
        return "";
    }

    const value = displayField ? option?.[displayField] : undefined;
    const fallbackValue = value ?? REFERENCE_LABEL_FALLBACK_FIELDS
        .map((field) => option?.[field])
        .find((candidate) => candidate !== null && candidate !== undefined && candidate !== "");

    if (fallbackValue === null || fallbackValue === undefined) {
        return "";
    }

    const display = resolveDisplayText(t, String(fallbackValue));
    const concreteClass = showConcreteClass && option?.sys_class_name
        ? resolveDisplayText(t, String(option.sys_class_label || option.sys_class_name)) : "";
    return concreteClass ? `${concreteClass} · ${display}` : display;
};

export function ReferenceAutocompleteInput({
    options,
    loading = false,
    selectedId,
    displayField = "",
    referenceTable,
    onChange,
    onSearchChange,
    onOpenOptions,
    error = false,
    helperText,
    autoFocus = false,
    showOpenButton = false,
    openButtonTitle = "Open selected record",
    disabled = false,
    fieldLabel,
    groupBy,
    showConcreteClass = false,
}: ReferenceAutocompleteInputProps) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");
    const [isSearching, setIsSearching] = useState(false);

    const selectedOption = useMemo(
        () => options.find((option) => option.id === selectedId) ?? null,
        [options, selectedId]
    );

    const hasUnavailableSelection = Boolean(selectedId) && !loading && !selectedOption;

    useEffect(() => {
        if (hasUnavailableSelection) {
            setInputValue(String(t("record.reference.unavailable")));
            return;
        }

        if (isSearching) {
            return;
        }

        if (!selectedOption) {
            if (!selectedId) {
                setInputValue("");
            }
            return;
        }

        setInputValue(getReferenceOptionLabel(t, selectedOption, displayField, showConcreteClass));
    }, [displayField, hasUnavailableSelection, isSearching, selectedId, selectedOption, showConcreteClass, t]);

    const restoreSelectedLabel = () => {
        setIsSearching(false);
        onSearchChange?.("");

        if (hasUnavailableSelection) {
            setInputValue(String(t("record.reference.unavailable")));
            return;
        }

        if (!selectedOption) {
            setInputValue("");
            return;
        }

        setInputValue(getReferenceOptionLabel(t, selectedOption, displayField, showConcreteClass));
    };

    const handleOpenReferenceRecord = (event: React.MouseEvent<HTMLElement>) => {
        if (!referenceTable || !selectedId) {
            return;
        }

        navigateFromClick(event, navigate, `/home/workspace/${referenceTable}/record/${selectedId}`);
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Autocomplete
                openText={fieldLabel ? `${t("Open")} ${fieldLabel}` : undefined}
                clearText={fieldLabel ? `${t("Clear")} ${fieldLabel}` : undefined}
                options={options}
                groupBy={groupBy}
                loading={loading}
                value={selectedOption}
                inputValue={inputValue}
                sx={{ flex: 1 }}
                disabled={disabled || hasUnavailableSelection}
                onOpen={onOpenOptions}
                onChange={(_, option) => {
                    onChange(option?.id ?? null);
                    setInputValue(getReferenceOptionLabel(t, option, displayField, showConcreteClass));
                    onSearchChange?.("");
                    setIsSearching(false);
                }}
                onInputChange={(_, value, reason) => {
                    if (reason === "reset") {
                        return;
                    }

                    setIsSearching(reason === "input" || reason === "clear");
                    setInputValue(value);
                    onSearchChange?.(value);
                }}
                getOptionLabel={(option) => getReferenceOptionLabel(t, option, displayField, showConcreteClass)}
                isOptionEqualToValue={(option, currentValue) => option.id === currentValue.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        autoFocus={autoFocus}
                        size="small"
                        fullWidth
                        error={error}
                        helperText={helperText}
                        onBlur={(event) => {
                            params.inputProps?.onBlur?.(event as any);
                            restoreSelectedLabel();
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading && <CircularProgress size={18} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
                            {showOpenButton && (
                <Tooltip title={openButtonTitle}>
                    <span>
                        <IconButton
                            size="small"
                            aria-label={fieldLabel ? `${t("Open")} ${fieldLabel}` : openButtonTitle}
                            title={fieldLabel ? `${t("Open")} ${fieldLabel}` : openButtonTitle}
                            disabled={!selectedId || !referenceTable || hasUnavailableSelection}
                            onClick={handleOpenReferenceRecord}
                        >
                            <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Box>
    );
}
